"use client";

import { FormEvent, useEffect, useState, useTransition } from "react";
import { Search } from "lucide-react";

import { usePathname, useRouter } from "@/i18n/navigation";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  ActiveListingType,
  ListingsSearchState,
  buildListingsSearchParams,
} from "./listings-state";
import { ListingsCardSection } from "@/types/pages/listings";

type ListingsControlsProps = {
  section: Pick<
    ListingsCardSection,
    "categories" | "default_sort" | "sort_options" | "ui"
  >;
  state: ListingsSearchState;
};

const typeTabs: ReadonlyArray<{
  id: ActiveListingType;
  labelKey: keyof ListingsCardSection["ui"];
  fallbackLabel: string;
}> = [
  {
    id: "all",
    labelKey: "type_all_label",
    fallbackLabel: "All",
  },
  {
    id: "persona",
    labelKey: "type_personas_label",
    fallbackLabel: "Personas",
  },
  {
    id: "skill",
    labelKey: "type_skills_label",
    fallbackLabel: "Skills",
  },
];

export default function ListingsControls({
  section,
  state,
}: ListingsControlsProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [draftQuery, setDraftQuery] = useState(state.query);

  useEffect(() => {
    setDraftQuery(state.query);
  }, [state.query]);

  const navigate = (overrides: Partial<ListingsSearchState>) => {
    const nextParams = buildListingsSearchParams(state, section, overrides);
    const nextPath = nextParams.toString()
      ? `${pathname}?${nextParams.toString()}`
      : pathname;

    startTransition(() => {
      router.replace(nextPath as any);
    });
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = (new FormData(event.currentTarget).get("q") || "").toString();

    navigate({
      query: query.trim(),
      page: 1,
    });
  };

  return (
    <>
      <form onSubmit={handleSearchSubmit} className="relative mx-auto mt-8 max-w-2xl">
        <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-primary/75" />
        <Input
          name="q"
          value={draftQuery}
          onChange={(event) => setDraftQuery(event.target.value)}
          placeholder={section.ui.search_placeholder}
          aria-label={section.ui.search_placeholder}
          className="h-12 rounded-xl border-white/14 bg-[linear-gradient(160deg,rgba(13,19,30,0.86),rgba(7,11,19,0.84))] pl-11 pr-24 text-foreground placeholder:text-muted-foreground focus-visible:ring-primary/45"
        />
        <Button
          type="submit"
          size="sm"
          className="landing-hero-button absolute right-2 top-1/2 h-8 -translate-y-1/2 rounded-lg border border-primary/45 bg-primary/14 text-primary hover:bg-primary/22"
          disabled={isPending}
        >
          Search
        </Button>
      </form>

      <section className="landing-section-divider border-y border-white/10 py-5">
        <div className="container space-y-5">
          <div className="flex flex-wrap gap-2">
            {typeTabs.map((tab) => {
              const isActive = state.activeType === tab.id;
              const label = section.ui[tab.labelKey] || tab.fallbackLabel;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() =>
                    navigate({
                      activeType: tab.id,
                      page: 1,
                    })
                  }
                  aria-pressed={isActive}
                  data-listing-type-tab={tab.id}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "border-cyan-300/40 bg-cyan-400/10 text-cyan-100"
                      : "border-white/14 bg-white/6 text-foreground/90 hover:border-cyan-300/25 hover:text-cyan-100"
                  }`}
                  disabled={isPending}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="overflow-x-auto">
            <div className="flex min-w-max gap-2 pb-1">
              {section.categories.map((category) => {
                const isActive = state.activeCategory === category.id;

                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() =>
                      navigate({
                        activeCategory: category.id,
                        page: 1,
                      })
                    }
                    data-listing-category-tab={category.id}
                    className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
                      isActive
                        ? "border-primary/60 bg-primary/16 text-[#ff8a7c]"
                        : "border-white/14 bg-white/6 text-foreground/90 hover:border-primary/40 hover:text-primary"
                    }`}
                    disabled={isPending}
                  >
                    {category.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
            <div className="flex items-center gap-2 text-sm text-foreground/90">
              <span>{section.ui.sort_label}:</span>
              <Select
                value={state.sortBy}
                onValueChange={(value) =>
                  navigate({
                    sortBy: value as ListingsSearchState["sortBy"],
                    page: 1,
                  })
                }
              >
                <SelectTrigger
                  aria-label={section.ui.sort_label}
                  className="h-9 w-[220px] rounded-lg border-white/14 bg-white/8 text-foreground"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {section.sort_options.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
